import { useAppConfigContext } from '@ltvco/refresh-lib/ctx';
import {
  Collapse,
  Skeleton,
  Stack,
  Text,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { ExpandLess, ExpandMore, PaymentsOutlined } from '@mui/icons-material';
import { useMemo, useState, type KeyboardEvent } from 'react';

import { useLLMPropertyProfessionalInsights } from 'hooks/useLLMPropertyProfessionalInsights';

import {
  getTagIcon,
  PROFESSIONAL_INSIGHT_SECTIONS,
} from './PropertyProfessionalInsights.constants';
import {
  AccordionHeader,
  DetailsSection,
  InsightBody,
  InsightIconCircle,
  InsightRow,
  InsightsCard,
  InsightsEmbed,
  InsightsHeader,
  InsightsList,
  OverviewInsightsWrap,
  SkeletonBlock,
  DisclaimerText,
  TagChip,
  ViewInReportLink,
} from './PropertyProfessionalInsights.styles';

import type {
  preforeclosureTypes,
  propertiesTypes,
} from '@ltvco/refresh-lib/v1';
// END IMPORTS

// START INTERFACES_AND_TYPES
type PropertyProfessionalInsightsProps = {
  property: propertiesTypes.Property;
  permalink: string;
  preforeclosures?: preforeclosureTypes.Preforeclosures;
  preforeclosuresSettled?: boolean;
};

export { OverviewInsightsWrap };

function scrollToReportSection(anchor: string) {
  const el = document.querySelector(anchor);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function PropertyProfessionalInsightsSkeleton() {
  return (
    <>
      <SkeletonBlock sx={{ height: 48, width: '100%' }} />
      <Stack direction="row" gap={1}>
        <Skeleton variant="rounded" width={196} height={32} />
        <Skeleton variant="rounded" width={137} height={32} />
      </Stack>
      <SkeletonBlock sx={{ height: 280, width: '100%' }} />
      <SkeletonBlock sx={{ height: 45, width: '100%' }} />
    </>
  );
}

export function PropertyProfessionalInsights({
  property,
  permalink,
  preforeclosures,
  preforeclosuresSettled = true,
}: PropertyProfessionalInsightsProps) {
  const theme = useTheme();
  const { trackEventGA4 } = useAppConfigContext();
  const [isExpanded, setIsExpanded] = useState(true);

  const { insights, isLoading, isError } = useLLMPropertyProfessionalInsights({
    property,
    permalink,
    preforeclosures,
    enabled: Boolean(property) && preforeclosuresSettled,
  });

  const handleAccordionToggle = () => {
    const nextExpanded = !isExpanded;
    setIsExpanded(nextExpanded);
    if (nextExpanded) {
      trackEventGA4({
        event_name: 'feature_engagement',
        interacted_from: 'professional_insights',
        type: 'expand',
      });
    }
  };

  const handleAccordionKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleAccordionToggle();
    }
  };

  const tagElements = useMemo(
    () =>
      insights?.tags.map((tag: string) => {
        const TagIcon = getTagIcon(tag);
        return <TagChip key={tag} icon={<TagIcon />} label={tag} />;
      }) ?? [],
    [insights?.tags]
  );

  const sectionElements = useMemo(
    () =>
      PROFESSIONAL_INSIGHT_SECTIONS.map(
        ({ key, title, Icon, reportAnchor }) => {
          const section = insights?.sections[key];
          if (!section) {
            return null;
          }

          return (
            <InsightRow key={key}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1.5}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  minWidth={0}
                >
                  <InsightIconCircle>
                    <Icon
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: 16,
                      }}
                    />
                  </InsightIconCircle>
                  <Text
                    variant="body1"
                    fontWeight={700}
                    sx={{ color: '#0d1b3e' }}
                  >
                    {title}
                  </Text>
                </Stack>
                <ViewInReportLink
                  variant="text"
                  onClick={() => scrollToReportSection(reportAnchor)}
                >
                  View In Report &gt;
                </ViewInReportLink>
              </Stack>
              <InsightBody>
                <Text
                  variant="body1"
                  sx={{ color: theme.palette.text.primary }}
                >
                  {section.description}
                </Text>
                {section.bullets?.length > 0 && (
                  <Text
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      mt: 0.5,
                    }}
                  >
                    {section.bullets.join(' • ')}
                  </Text>
                )}
              </InsightBody>
            </InsightRow>
          );
        }
      ),
    [insights?.sections, theme]
  );

  if (isError && !insights) {
    return null;
  }

  if (!isLoading && !insights) {
    return null;
  }

  return (
    <InsightsEmbed id="property-professional-insights">
      <InsightsCard>
        <InsightsHeader>
          <PaymentsOutlined sx={{ fontSize: 24 }} />
          <Text variant="body1" fontWeight={700} sx={{ fontSize: 18, m: 0 }}>
            Insights for Professionals
          </Text>
        </InsightsHeader>

        {!insights ? (
          <PropertyProfessionalInsightsSkeleton />
        ) : (
          <>
            <Text variant="body1" sx={{ color: theme.palette.text.primary }}>
              {insights.headerDescription}
            </Text>

            {tagElements.length > 0 && (
              <Stack direction="row" gap={1} flexWrap="wrap">
                {tagElements}
              </Stack>
            )}
            <Text
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: 12,
                fontFamily: 'Lato',
              }}
            >
              AI-generated summary based on report data for informational
              purposes only and may contain errors.
            </Text>

            <DetailsSection>
              <AccordionHeader
                onClick={handleAccordionToggle}
                onKeyDown={handleAccordionKeyDown}
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
              >
                <Text
                  variant="body1"
                  fontWeight={700}
                  sx={{ color: theme.palette.primary.main }}
                >
                  Explore the records
                </Text>
                {isExpanded ? (
                  <ExpandLess sx={{ color: theme.palette.primary.main }} />
                ) : (
                  <ExpandMore sx={{ color: theme.palette.primary.main }} />
                )}
              </AccordionHeader>

              <Collapse in={isExpanded}>
                <InsightsList>{sectionElements}</InsightsList>
              </Collapse>
              {isExpanded && (
                <DisclaimerText>
                  This section was generated by AI using publicly available
                  property records and data compiled in this report. While we
                  strive for accuracy, AI-generated content may contain errors,
                  omissions, or outdated information. It is provided for
                  informational purposes only, does not constitute legal,
                  financial, or professional advice, and should not be relied
                  upon as a substitute for independent verification of public
                  records.
                </DisclaimerText>
              )}
            </DetailsSection>
          </>
        )}
      </InsightsCard>
    </InsightsEmbed>
  );
}
// END RENDER
